/**
 * vanilla 마운트 조립 루트(composition root) — mountLumirEditor.
 * 관심사별 모듈(./editor/*)을 명시적 의존성 주입으로 결선한다. 각 모듈은 독립적이라
 * 한 기능 수정이 다른 기능으로 번지지 않는다(공유 클로저 상태 최소화).
 *
 *   const editor = mountLumirEditor(containerEl, { initialContent, onContentChange });
 *   editor.getDocument(); editor.undo(); editor.redo(); editor.destroy();
 *
 *  - undo/redo(./editor/history): JSON 스냅샷 히스토리(동일 스냅샷 코얼레싱).
 *  - IME 가드: compositionstart~end 동안 커밋/알림 보류(한글 조합 1회당 undo 1스텝).
 */

import { blocksToDom, domToBlocks, renderBlock, elToBlock, CUSTOM_BLOCK_TYPES } from './serialize/document.js';
import { liftFontSize } from './serialize/font-size.js';
import {
  attachEditing,
  tryMarkdown,
  tryInlineMarkdown,
  normalizeCodeTrailingNewline,
} from './editing.js';
import { blocksToMarkdown, markdownToBlocks } from './serialize/markdown.js';
import { blocksToHtml, blocksToFullHtml } from './serialize/html.js';
import { attachSlashMenu } from './ui/slash-menu.js';
import { COLUMNS_DIVIDER_ICON } from './ui/icons.js';
import { attachEmojiPicker } from './ui/emoji-picker.js';
import { attachTableSizePicker } from './ui/table-size-picker.js';
import { attachFormatToolbar } from './ui/format-toolbar.js';
import { attachTableControls } from './table/table-controls.js';
import { createS3Uploader } from './upload/s3-uploader.js';
import { isImageFile, isVideoFile, isAudioFile } from './upload/file-validation.js';
import { attachFloatingMenu } from './ui/floating-menu.js';
import { attachSideMenu } from './ui/side-menu.js';
import { attachColumnControls } from './columns/column-controls.js';
import { registry } from './features/index.js'; // 기능 슬라이스(스펙 레지스트리) — 슬래시 등 기여 수집
import { attachLinkToolbar } from './ui/link-toolbar.js';
import { attachFilePanel } from './ui/file-panel.js';
import {
  parseHtmlToBlocks,
  blocksFromText,
  fitColumnWidths,
  isBareUrl,
  hasUrl,
  linkifyToInline,
} from './paste/paste-html.js';
import { normalizeExcelClipboardHtml } from './paste/excel-format.js';
import { rerenderTable } from './table/render.js';
import { LumirEditorError } from './errors/lumir-error.js';
import { resolveLocale } from './locale.js';
import { readActiveMarks, applyLink, insertInlineAtCursor, applyInlineMarkAcross, readActiveMarksAcross, setSelection } from './inline-format.js';
import { resolveEmbed } from './utils/url.js';

// 관심사별 추출 모듈
import {
  DEFAULT_BLOCK_PROPS,
  resolveInitialContent,
  buildMediaBlock,
  emptyTable,
  emptyColumns,
} from './editor/blocks.js';
import { createErrorReporter } from './editor/error-toast.js';
import { createUploadOverlay } from './editor/upload-overlay.js';
import {
  applyEditable as applyEditableDom,
  normalizeColumnsDom as normalizeColumnsDomEl,
  applyResolveFileUrl as applyResolveFileUrlEl,
} from './editor/dom-ops.js';
import { createBlurHighlight } from './editor/blur-highlight.js';
import { createMediaDeletionDetector } from './editor/media-detect.js';
import { createHistory } from './editor/history.js';
import { createPlaceholders } from './editor/placeholder.js';
import { attachCaretNav } from './editor/caret-nav.js';
import { attachBlockSelect, LUMIR_BLOCK_COPY_MIME } from './editor/block-select.js';
import { attachCrossBlockSelect } from './editor/cross-block-select.js';
import { attachCrossBlockEdit } from './editor/cross-block-edit.js';
import { deleteRangeAcross } from './editor/cross-block-range.js';
import { contentLen, crossBoundingRect } from './editor/cross-block-selection.js';

/** editorEl 상위(자신 포함)에서 세로 스크롤 컨테이너를 찾는다. 없으면 null(=페이지/window 스크롤). */
function getScrollParent(node) {
  let el = node;
  const getStyle = typeof getComputedStyle === 'function' ? getComputedStyle : null;
  while (el && el.nodeType === 1 && el !== document.body && el !== document.documentElement) {
    const oy = getStyle ? getStyle(el).overflowY : '';
    if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return el;
    el = el.parentElement;
  }
  return null;
}

/** 비동기 Clipboard 경로가 text/html data 속성에 임베드한 무손실 JSON 회수(커스텀 MIME 미지원 대비). */
function extractEmbeddedBlocks(html) {
  if (!html || typeof DOMParser === 'undefined' || html.indexOf('data-lumir-block-copy') === -1) return '';
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector('[data-lumir-block-copy]');
    return el ? decodeURIComponent(el.getAttribute('data-lumir-block-copy') || '') : '';
  } catch (_) { return ''; }
}

// 붙여넣기 정제(화이트리스트) — 제네릭 텍스트 타입 ∪ 커스텀(레지스트리) 타입. 미지 타입은 드롭.
const GENERIC_BLOCK_TYPES = new Set([
  'paragraph', 'heading', 'bulletListItem', 'numberedListItem', 'checkListItem',
  'toggleListItem', 'quote', 'codeBlock', 'divider',
]);
const KNOWN_PASTE_TYPES = new Set([...GENERIC_BLOCK_TYPES, ...CUSTOM_BLOCK_TYPES]);

/** columnList 자식 컬럼 정제 — 이 모델의 컬럼은 type 필드가 없다({props,children}). id는 생략. */
function sanitizeColumn(c) {
  if (!c || typeof c !== 'object') return null;
  return { props: c.props, children: Array.isArray(c.children) ? sanitizeBlocks(c.children) : [] };
}
/**
 * 신뢰 경계: 붙여넣기 JSON을 알려진 타입 화이트리스트로 정제 + 전 레벨 id 제거(복제 시 data-id 중복 방지).
 * props/content는 렌더 시 무해화(inline escape·neutralizeUrl·isAllowedEmbedUrl)되므로 verbatim 보존.
 */
function sanitizeBlocks(arr) {
  const out = [];
  for (const b of Array.isArray(arr) ? arr : []) {
    if (!b || typeof b !== 'object' || typeof b.type !== 'string') continue;
    if (b.type === 'columnList') {
      const cols = (Array.isArray(b.children) ? b.children : []).map(sanitizeColumn).filter(Boolean);
      out.push({ type: 'columnList', props: b.props, content: b.content, children: cols });
    } else if (KNOWN_PASTE_TYPES.has(b.type)) {
      const n = { type: b.type };
      if (b.props !== undefined) n.props = b.props;
      if (b.content !== undefined) n.content = b.content;
      n.children = Array.isArray(b.children) ? sanitizeBlocks(b.children) : [];
      out.push(n);
    }
  }
  return out;
}

export function mountLumirEditor(container, opts) {
  if (typeof document === 'undefined')
    throw new Error('mountLumirEditor: DOM(document) 필요');
  opts = opts || {};

  const editorEl = document.createElement('div');
  // "bn-editor"는 BlockNote 클래스 별칭 — 소비처의 [&_.bn-editor]:… 등 기존 CSS 오버라이드 호환.
  editorEl.className = 'lumir-editor bn-editor';
  const _initial = resolveInitialContent(opts);
  blocksToDom(_initial, editorEl);
  if (container) container.appendChild(editorEl);

  // 초기 표 폭 채움(fill): 콘텐츠 폭보다 좁게 들어온 표(다른 폭 기준으로 만든 표·구버전 import 등)를
  // 콘텐츠 폭에 맞춰 열폭을 비례 확대한다. 이미 채웠거나 더 넓은 표(→ CSS max-width/편집 시 fit 이 축소)는 건드리지 않는다.
  // 열폭이 일부라도 auto(null) 이면 CSS 자동분배에 맡기고 건너뛴다. 모바일(가로 스크롤 정책)은 제외.
  function fillInitialTablesToWidth(root) {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 640px)').matches) return;
    root.querySelectorAll('.lumir-tbl').forEach((table) => {
      if (!table.__model) return;
      const content = table.parentElement; // .content
      // .content 는 좌우 padding 0(table.css) → 콘텐츠박스 폭 = clientWidth. 이 폭까지 채우면 표가 좌·우변 모두
      // 콘텐츠 끝선에 flush(= 네이티브 표와 동일 폭·동일 정렬).
      const avail = content && content.clientWidth ? content.clientWidth : 0;
      if (!(avail > 0)) return;
      const cw = table.__model.columnWidths;
      if (!Array.isArray(cw) || !cw.length || cw.some((w) => !w)) return;
      const total = cw.reduce((a, b) => a + (b || 0), 0);
      if (total <= 0 || total >= avail) return;
      const f = avail / total;
      const next = cw.map((w) => Math.round(w * f));
      next[next.length - 1] += avail - next.reduce((a, b) => a + b, 0); // 반올림 잔차 보정 → 합=avail
      table.__model.columnWidths = next;
      rerenderTable(table);
    });
  }
  fillInitialTablesToWidth(editorEl);

  // A8: 표시용 URL 해석 래퍼(원본 __props.url 보존 → 직렬화 무영향).
  const applyResolveFileUrl = () =>
    applyResolveFileUrlEl(editorEl, opts.resolveFileUrl);
  applyResolveFileUrl();

  let onChange =
    typeof opts.onContentChange === 'function' ? opts.onContentChange : null;
  const onSelectionChange =
    typeof opts.onSelectionChange === 'function'
      ? opts.onSelectionChange
      : null;
  const L = resolveLocale(opts.locale);

  // --- 코어 props (P7): className / theme / placeholder / editable ---
  if (typeof opts.className === 'string' && opts.className)
    editorEl.className += ' ' + opts.className;
  // theme: 문자열("light"/"dark"/커스텀)만 data-theme로. 원본의 객체 테마(Mantine)는 1:1 불가 →
  // 객체는 dark 의도만 감지하고 그 외 기본(light). "[object Object]" 직렬화 방지(호환 하드닝).
  function applyThemeOpt(theme) {
    if (typeof theme === 'string' && theme)
      editorEl.setAttribute('data-theme', theme);
    else if (theme && typeof theme === 'object')
      editorEl.setAttribute('data-theme', theme.dark ? 'dark' : 'light');
    else editorEl.removeAttribute('data-theme');
  }
  if (opts.theme) applyThemeOpt(opts.theme);
  if (opts.columnDivider) editorEl.classList.add('lumir-col-divider');
  // --- 구성 토글 (Tier-2) ---
  const cfgTables = opts.tables || {};
  const headingLevels =
    opts.heading &&
    Array.isArray(opts.heading.levels) &&
    opts.heading.levels.length
      ? opts.heading.levels.slice()
      : [1, 2, 3, 4, 5, 6];
  const trailingBlock = opts.trailingBlock !== false; // 끝에 자동 빈 문단 유지
  const tabBehavior =
    opts.tabBehavior === 'prefer-indent'
      ? 'prefer-indent'
      : 'prefer-navigate-ui';
  const placeholderText =
    opts.placeholder != null ? String(opts.placeholder) : L.placeholder;
  let editable = opts.editable !== false;

  // --- 업로드 설정 (우선순위: uploadFile > s3Upload > 없음) ---
  const onImageDelete =
    typeof opts.onImageDelete === 'function' ? opts.onImageDelete : null;

  // 에러 토스트 + LumirEditorError 정규화 리포터
  const errorReporter = createErrorReporter(opts);
  const onError = errorReporter.onError;

  // 업로드 진행률 오버레이 + 업로더
  const uploadOverlay = createUploadOverlay(L);
  const showProgress = uploadOverlay.showProgress;
  const hideProgress = uploadOverlay.hideProgress;
  let uploader = null;
  if (typeof opts.uploadFile === 'function') uploader = opts.uploadFile;
  else if (opts.s3Upload) {
    const cfg = { ...opts.s3Upload };
    const userProg = cfg.onProgress;
    cfg.onProgress = (p) => {
      showProgress(p);
      if (typeof userProg === 'function') userProg(p);
    };
    uploader = createS3Uploader(cfg);
  }

  // --- 직렬화 ---
  function getDocument() {
    return domToBlocks(editorEl);
  }

  // --- placeholder + 읽기전용 (P7) ---
  const placeholders = createPlaceholders({
    editorEl,
    getEditable: () => editable,
    placeholderText,
  });
  const refreshPlaceholders = placeholders.refresh;
  const focusedContent = placeholders.focusedContent;
  const applyEditable = (on) => applyEditableDom(editorEl, on);

  // --- 히스토리(undo/redo): 스택은 createHistory 소유, DOM 복원(restore)·알림(fireChange) 주입 ---
  function restore(snap) {
    // M1: 재구축 전, 열린 임시 UI(메뉴/툴바)가 곧 detached될 노드를 가리키지 않도록 닫는다.
    if (slash) slash.close();
    if (blockSelect) blockSelect.clear(); // 재구축 전 블록 선택 모드 해제(곧 detached될 노드 참조 정리)
    if (crossSelect) crossSelect.clear(); // 크로스블록 선택도 해제(detached될 섬 참조/하이라이트 정리)
    if (linkToolbar) linkToolbar.hide();
    if (sideMenu) {
      sideMenu.hide();
      if (sideMenu.closeDragMenu) sideMenu.closeDragMenu();
    }
    if (emojiPicker && emojiPicker.close) emojiPicker.close();
    // 스크롤 위치 보존: blocksToDom이 innerHTML을 비우면 스크롤 컨테이너 높이가 순간 0으로 붕괴되며
    // 최상단으로 튄다(undo/redo 시 시점 점프). 재구축 전 위치를 담아 두었다가 복원한다.
    const scroller = getScrollParent(editorEl);
    const sTop = scroller ? scroller.scrollTop : 0;
    const sLeft = scroller ? scroller.scrollLeft : 0;
    const winX = typeof window !== 'undefined' ? window.scrollX : 0;
    const winY = typeof window !== 'undefined' ? window.scrollY : 0;
    blocksToDom(snap, editorEl); // DOM 재구축(fontSize lift 포함). 리스너는 editorEl에 있어 유지됨.
    applyEditable(editable); // 재렌더 후 읽기전용/placeholder 재적용
    refreshPlaceholders();
    applyResolveFileUrl(); // A8: 재렌더 후 표시용 URL 재해석
    refreshFeatureBlocks(); // 재구축 후 기능 컨트롤러 재스캔(setBlocks 경로와 정합)
    // 스크롤 복원(레이아웃 확정 후). 컨테이너·페이지 스크롤 모두 원복. 위치가 실제로 바뀐 경우에만.
    if (scroller) {
      if (scroller.scrollTop !== sTop) scroller.scrollTop = sTop;
      if (scroller.scrollLeft !== sLeft) scroller.scrollLeft = sLeft;
    }
    if (
      typeof window !== 'undefined' && typeof window.scrollTo === 'function' &&
      (window.scrollX !== winX || window.scrollY !== winY)
    ) {
      try { window.scrollTo(winX, winY); } catch (_) {}
    }
  }
  const { commit, undo, redo, canUndo, canRedo } = createHistory({
    getDocument,
    restore,
    fireChange,
  });
  commit(); // 초기 스냅샷

  // --- 변경 알림 + IME 가드 ---
  let composing = false;
  const detectDeletedMedia = createMediaDeletionDetector(
    onImageDelete,
    opts.initialContent || [],
  );
  function fireChange() {
    const doc = getDocument();
    detectDeletedMedia(doc);
    refreshPlaceholders();
    if (onChange) onChange(doc);
  }

  function handleEdit(e) {
    if (!editable) return;
    if (composing) return; // 조합 중에는 커밋/알림 보류(IME 가드)
    const content =
      e && e.target && e.target.closest && e.target.closest('.content');
    if (
      content &&
      editorEl.contains(content) &&
      !(content.closest && content.closest('.lumir-tbl'))
    ) {
      const b = content.closest('.block');
      // 마크다운 `---` → 페이지 나누기(none-content 블록이라 changeBlockType 경로 대신 직접 삽입)
      if (
        b &&
        b.dataset.type === 'paragraph' &&
        content.textContent === '---'
      ) {
        content.textContent = '';
        try {
          const r = document.createRange();
          r.selectNodeContents(content);
          r.collapse(true);
          const s = window.getSelection();
          s.removeAllRanges();
          s.addRange(r);
        } catch (_) {}
        insertPageBreak(); // 내부에서 commit/fireChange 수행
        return;
      }
      // 블록 마크다운(시작 트리거) 우선, 아니면 인라인 마크다운(닫는 구분자) 시도
      if (!tryMarkdown(content, headingLevels)) tryInlineMarkdown(content);
      // 코드블록: 타이핑 중 후행 개행 filler 동기화(빈 마지막 줄 렌더 보정)
      if (b && b.dataset.type === 'codeBlock')
        normalizeCodeTrailingNewline(content);
    }
    commit();
    fireChange();
  }
  editorEl.addEventListener('compositionstart', () => {
    composing = true;
    // 크로스 선택 위에서 IME 조합 시작 → 선택 삭제 후 join 캐럿에서 조합 진행(compositionend가 1회 커밋).
    if (crossSelect && crossSelect.isActive()) {
      const caret = deleteRangeAcross(editorEl, crossSelect.get());
      crossSelect.clear();
      if (caret && caret.island) {
        try {
          caret.island.focus();
          setSelection(caret.island, caret.offset, caret.offset);
        } catch (_) {}
      }
    }
  });
  editorEl.addEventListener('compositionend', () => {
    composing = false;
    commit(); // 조합 결과를 1스텝으로 커밋
    fireChange();
  });
  editorEl.addEventListener('input', handleEdit);

  // 토글(접을 수 있는) 블록 캐럿 클릭 → children 접기/펼치기(휘발성 UI 상태, 직렬화 안 함)
  editorEl.addEventListener('mousedown', (e) => {
    const caret = e.target.closest && e.target.closest('.lumir-toggle');
    if (!caret) return;
    e.preventDefault(); // 캐럿 클릭이 캐럿 포커스/선택 이동을 일으키지 않도록
    const b = caret.closest('.block');
    if (b) b.classList.toggle('collapsed');
  });

  // 체크리스트 체크박스 클릭 → checked 토글(props.checked 직렬화 + 변경 알림)
  editorEl.addEventListener('mousedown', (e) => {
    if (!editable) return;
    const chk = e.target.closest && e.target.closest('.marker .check, .marker');
    if (!chk) return;
    const b = chk.closest && chk.closest('.block[data-type="checkListItem"]');
    if (!b) return;
    e.preventDefault();
    const now = !b.classList.contains('checked');
    b.classList.toggle('checked', now);
    if (!b.__props)
      b.__props = {
        textColor: 'default',
        backgroundColor: 'default',
        textAlignment: 'left',
      };
    b.__props.checked = now;
    structuralChange();
  });

  // Undo/Redo 키보드 (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z, Ctrl+Y) — 네이티브 contenteditable undo가
  // JSON 스냅샷 히스토리와 충돌하지 않도록 가로채 자체 히스토리로 처리(표 셀 포함 에디터 전역).
  function focusAfterRestore() {
    const c = editorEl.querySelector(
      ".content[contenteditable='true'], td.lumir-cell[contenteditable='true']",
    );
    if (c) {
      try {
        c.focus({ preventScroll: true }); // 첫 편집요소 포커스가 뷰를 최상단으로 스크롤하지 않도록(undo/redo 시점 점프 방지)
        const r = document.createRange();
        r.selectNodeContents(c);
        r.collapse(false);
        const s = window.getSelection();
        s.removeAllRanges();
        s.addRange(r);
      } catch (_) {}
    }
  }
  editorEl.addEventListener('keydown', (e) => {
    if (!editable) return;
    if (e.isComposing || composing) return; // IME 가드
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const k = (e.key || '').toLowerCase();
    if (k === 'z' && !e.shiftKey) {
      e.preventDefault();
      if (undo()) focusAfterRestore();
    } else if ((k === 'z' && e.shiftKey) || k === 'y') {
      e.preventDefault();
      if (redo()) focusAfterRestore();
    } else if (k === 'k') {
      e.preventDefault();
      if (linkToolbar) linkToolbar.promptAdd();
    } // 선택 텍스트에 링크 추가
  });

  // --- 업로드 파이프라인 (P5-③): 드롭/붙여넣기/슬래시/버튼 → 업로드 → 미디어 블록 삽입 ---
  function currentBlock() {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const node = sel.getRangeAt(0).startContainer;
    const el = node.nodeType === 1 ? node : node.parentElement;
    return el ? el.closest('.block') : null;
  }
  function insertMediaBlock(type, url, name) {
    const blockObj = buildMediaBlock(type, url, name);
    const newEl = renderBlock(blockObj);
    const anchor = currentBlock() || editorEl.lastElementChild;
    if (anchor && anchor.classList && anchor.classList.contains('block')) {
      const c = anchor.querySelector('.content');
      const emptyPara =
        anchor.dataset.type === 'paragraph' && c && c.textContent.trim() === '';
      if (emptyPara) anchor.replaceWith(newEl);
      else anchor.after(newEl);
    } else {
      editorEl.appendChild(newEl);
    }
    if (trailingBlock && !newEl.nextElementSibling)
      newEl.after(
        renderBlock({
          type: 'paragraph',
          props: DEFAULT_BLOCK_PROPS(),
          content: [],
          children: [],
        }),
      );
    applyResolveFileUrl(); // A8
    commit();
    fireChange();
  }
  async function insertFile(file) {
    // 일반 파일 업로드는 차단(이미지/비디오/오디오만 — 비디오/오디오는 allow 옵션 필요)
    const img = isImageFile(file, opts.maxImageFileSize);
    const vid =
      opts.allowVideoUpload && isVideoFile(file, opts.maxVideoFileSize);
    const aud =
      opts.allowAudioUpload && isAudioFile(file, opts.maxAudioFileSize);
    if (!img && !vid && !aud) {
      onError(
        LumirEditorError.invalidFileType(
          file && file.name ? file.name : 'file',
          !!opts.allowVideoUpload,
        ),
      );
      return;
    }
    if (!uploader) {
      // 업로더(uploadFile/s3Upload) 미설정 = 이미지 업로드 비활성. 개발자용 설정 에러가 아니라
      // 사용자용 안내 토스트만 띄우고 조용히 무시(콘솔 에러·onError 콜백 발생 안 함).
      errorReporter.showToast(L.upload.disabled);
      return;
    }
    showProgress(0);
    const kind = img ? 'image' : vid ? 'video' : 'audio';
    if (typeof opts.onUploadStart === 'function') {
      try {
        opts.onUploadStart({ file, kind });
      } catch (_) {}
    } // A9
    let uploadedUrl, uploadErr;
    try {
      uploadedUrl = await uploader(file);
      insertMediaBlock(kind, uploadedUrl, file && file.name);
    } catch (e) {
      uploadErr =
        e instanceof Error
          ? e
          : new Error(e && e.message ? e.message : '업로드 실패');
      onError(LumirEditorError.uploadFailed(uploadErr.message, uploadErr));
    } finally {
      hideProgress();
      if (typeof opts.onUploadEnd === 'function') {
        try {
          opts.onUploadEnd({ file, kind, url: uploadedUrl, error: uploadErr });
        } catch (_) {}
      } // A9
    }
  }
  async function insertFiles(files) {
    for (const f of files) await insertFile(f);
  }
  const isMedia = (f) =>
    isImageFile(f, opts.maxImageFileSize) ||
    (opts.allowVideoUpload && isVideoFile(f, opts.maxVideoFileSize)) ||
    (opts.allowAudioUpload && isAudioFile(f, opts.maxAudioFileSize));
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  // 일반 파일 업로드 차단: 항상 이미지(+허용 시 비디오/오디오)만 선택 가능
  fileInput.accept = [
    'image/*',
    opts.allowVideoUpload && 'video/*',
    opts.allowAudioUpload && 'audio/*',
  ]
    .filter(Boolean)
    .join(',');
  fileInput.style.display = 'none';
  fileInput.addEventListener('change', () => {
    const files = fileInput.files ? [...fileInput.files] : [];
    fileInput.value = '';
    if (files.length) insertFiles(files);
  });
  function openImagePicker() {
    fileInput.click();
  }
  editorEl.addEventListener('drop', (e) => {
    if (!editable) return;
    const dt = e.dataTransfer;
    const files = dt && dt.files ? [...dt.files] : [];
    const media = files.filter(isMedia);
    if (media.length) {
      e.preventDefault();
      insertFiles(media);
      return;
    } // 데스크톱 이미지 파일 → 업로드
    // 파일이 아닌 드래그(웹페이지 이미지/URL/HTML) → html/text 경로(이미지·표 등 구조 파싱)
    const html = dt && dt.getData ? dt.getData('text/html') : '';
    const text =
      dt && dt.getData
        ? dt.getData('text/uri-list') || dt.getData('text/plain') || ''
        : '';
    if ((html || text) && handlePasteContent(html, text)) e.preventDefault();
  });
  editorEl.addEventListener('dragover', (e) => {
    if (!editable) return;
    const types =
      e.dataTransfer && e.dataTransfer.types ? [...e.dataTransfer.types] : [];
    // 파일뿐 아니라 웹 이미지/URL 드래그(text/html·uri-list)도 drop을 받도록 기본동작 차단
    if (
      types.includes('Files') ||
      types.includes('text/html') ||
      types.includes('text/uri-list')
    )
      e.preventDefault();
  });
  function insertBlocksAtCursor(blocks) {
    if (!blocks || !blocks.length) return;
    const els = blocks.map(renderBlock);
    const anchor = currentBlock() || editorEl.lastElementChild;
    let last = null;
    if (anchor && anchor.classList && anchor.classList.contains('block')) {
      const c = anchor.querySelector('.content');
      const emptyPara =
        anchor.dataset.type === 'paragraph' && c && c.textContent.trim() === '';
      let ref = anchor;
      els.forEach((el, i) => {
        if (i === 0 && emptyPara) anchor.replaceWith(el);
        else ref.after(el);
        ref = el;
        last = el;
      });
    } else {
      els.forEach((el) => {
        editorEl.appendChild(el);
        last = el;
      });
    }
    if (trailingBlock && last && !last.nextElementSibling)
      last.after(
        renderBlock({
          type: 'paragraph',
          props: DEFAULT_BLOCK_PROPS(),
          content: [],
          children: [],
        }),
      );
    els.forEach((el) => {
      const tables =
        el.dataset && el.dataset.type === 'table'
          ? [el]
          : [...el.querySelectorAll('.block[data-type="table"]')]; // 컬럼 등 중첩 표 포함
      tables.forEach(fitTableToWidth);
    }); // 넓은표 폭 맞춤
    refreshFeatureBlocks(); // 붙여넣은 기능 블록 컨트롤러 갱신
    commit();
    fireChange();
    return els; // 삽입된 최상위 블록 엘리먼트(붙여넣기 이미지 비동기 업로드가 이미지 el을 찾는 데 사용)
  }

  // --- 붙여넣기 미디어 처리 (Word 등 HTML 경로) ---
  // DnD/이미지 파일 복붙은 insertFile→uploader 로 서버 업로드되지만, HTML(<img>) 경로는 src 를 그대로
  // 참조만 해왔다. 그 결과 data:(base64) 미디어가 문서 JSON 에 통째로 저장되어 DB 비대·저장 실패·성능
  // 저하를 유발하고, file://(원본 PC 로컬)은 타 PC/배포에서 로드 불가였다.
  // 정책: base64/blob/로컬 미디어는 절대 문서에 남기지 않는다.
  //  - 웹 URL(http(s)/상대) → 그대로 유지
  //  - data:image//blob: 이미지 + uploader 있음 → 삽입 후 업로드하여 서버 URL 로 교체(실패 시 제거)
  //  - 그 외(uploader 없음·file://·data:비이미지·data/blob 영상 등) → 삽입하지 않고 제외
  const MEDIA_TYPES = new Set(['image', 'video', 'audio']);
  const PASTE_IMG_UPLOADABLE = /^(?:data:image\/|blob:)/i;
  /** 문서에 그대로 남겨도 되는 미디어 URL 인지(웹 접근 가능). data:/blob:/file: 은 불가. */
  function isPersistableMediaUrl(url) {
    return !/^(?:data:|blob:|file:|filesystem:)/i.test(String(url == null ? '' : url).trim());
  }
  /**
   * 붙여넣기 블록에서 문서에 남기면 안 되는 미디어(base64/blob/로컬)를 제거. { blocks, dropped }.
   * data:image//blob: 이미지는 uploader 가 있을 때만 남긴다(직후 uploadPastedImages 가 서버 URL 로 승격).
   */
  function filterPasteMedia(blocks) {
    let dropped = 0;
    const walk = (arr) =>
      arr.filter((b) => {
        if (b && MEDIA_TYPES.has(b.type) && b.props && b.props.url) {
          const url = b.props.url;
          if (isPersistableMediaUrl(url)) return true; // 웹 URL — 유지
          if (uploader && b.type === 'image' && PASTE_IMG_UPLOADABLE.test(String(url))) return true; // 업로드 예정
          dropped++;
          return false; // base64/blob/로컬 → 문서에 남기지 않음
        }
        if (b && Array.isArray(b.children) && b.children.length) b.children = walk(b.children);
        return true;
      });
    return { blocks: walk(blocks), dropped };
  }
  /**
   * 삽입된 이미지 블록 중 data:/blob: 을 uploader 로 업로드해 서버 URL 로 교체(비동기, 제자리 스왑).
   * 업로드/검증/fetch 실패 시 해당 블록을 제거해 base64 가 문서에 남지 않도록 한다.
   */
  async function uploadPastedImages(els) {
    if (!uploader || !els || !els.length || typeof fetch !== 'function') return;
    const targets = els.filter(
      (el) =>
        el && el.dataset && el.dataset.type === 'image' && el.__props && PASTE_IMG_UPLOADABLE.test(String(el.__props.url || '')),
    );
    if (!targets.length) return;
    showProgress(0);
    let changed = false;
    const removeBlock = (el) => { if (el && el.parentNode) { el.remove(); changed = true; } };
    await Promise.all(
      targets.map(async (el) => {
        const src = el.__props.url;
        try {
          const res = await fetch(src);
          const blob = await res.blob();
          const extn = (blob.type && blob.type.split('/')[1]) || 'png';
          const file = new File([blob], `pasted-image.${extn}`, { type: blob.type || 'image/png' });
          if (!isImageFile(file, opts.maxImageFileSize)) { removeBlock(el); return; } // SVG·용량·타입 실패 → base64 제거
          if (typeof opts.onUploadStart === 'function') { try { opts.onUploadStart({ file, kind: 'image' }); } catch (_) {} }
          let url, uploadErr;
          try { url = await uploader(file); }
          catch (e) { uploadErr = e instanceof Error ? e : new Error(e && e.message ? e.message : '업로드 실패'); }
          if (typeof opts.onUploadEnd === 'function') { try { opts.onUploadEnd({ file, kind: 'image', url, error: uploadErr }); } catch (_) {} }
          if (uploadErr) { onError(LumirEditorError.uploadFailed(uploadErr.message, uploadErr)); removeBlock(el); return; } // 실패 → base64 제거
          if (url && editorEl.contains(el)) {
            el.__props.url = url; // 직렬화(imageElementToBlock)가 읽는 원천
            const img = el.querySelector('img'); if (img) img.src = url;
            const card = el.querySelector('a.lumir-image-namecard'); if (card) card.href = url;
            changed = true;
          } else if (!url) {
            removeBlock(el); // URL 미반환 → base64 제거
          }
        } catch (_) { removeBlock(el); /* fetch 실패(CORS·blob 만료 등) → base64 제거 */ }
      }),
    );
    hideProgress();
    if (changed) { applyResolveFileUrl(); commit(); fireChange(); }
  }
  /**
   * 무손실 lumir-JSON(다중 블록 복제) 붙여넣기: 정제 → fontSize lift → 미디어 필터 → 삽입 → (중첩)이미지 업로드.
   * 소비(consumed)되면 true(네이티브 폴백 차단). 파싱/정제 실패 시 false로 기존 HTML 경로에 위임.
   */
  function handleLumirBlocksPaste(json) {
    let parsed;
    try { parsed = JSON.parse(json); } catch (_) { return false; }
    if (!Array.isArray(parsed)) return false;
    const clean = sanitizeBlocks(parsed);
    if (!clean.length) return false;
    // 저장 폼(형제키) → 에디터 폼(styles.fontSize). insertBlocksAtCursor→renderBlock은 lift 안 하므로 필수.
    const lifted = liftFontSize(clean);
    const { blocks: kept, dropped } = filterPasteMedia(lifted);
    if (!kept.length && !dropped) return false;
    if (blockSelect && blockSelect.isActive()) blockSelect.clear(); // 활성 블록선택 하이라이트 정리
    const els = insertBlocksAtCursor(kept);
    // data:/blob: 이미지를 서버 URL로 승격 — 컬럼 등 중첩 내부 이미지까지 수집(top-level만 스캔하면 누락).
    const imgEls = (els || []).flatMap((el) => [el, ...el.querySelectorAll('.block[data-type="image"]')]);
    uploadPastedImages(imgEls);
    if (dropped) errorReporter.showToast(L.paste.imagesDropped);
    return true;
  }
  /**
   * 그립 메뉴 "블록 복제": 단일 블록 DOM → 객체(표/컬럼/기능 포함) → 새 id 없는 DOM → 원본 바로 뒤 삽입.
   * elToBlock/renderBlock은 에디터 폼(styles.fontSize)이라 lift/lower 불필요. sanitizeBlocks로 id 제거.
   */
  function duplicateBlockEl(el) {
    if (!editable || !el || !editorEl.contains(el)) return;
    const [clean] = sanitizeBlocks([elToBlock(el)]); // 자기 블록이라 화이트리스트는 무손실, id만 제거
    if (!clean) return;
    const newEl = renderBlock(clean);
    el.after(newEl);
    refreshFeatureBlocks();
    const tables =
      newEl.dataset && newEl.dataset.type === 'table'
        ? [newEl]
        : [...newEl.querySelectorAll('.block[data-type="table"]')];
    tables.forEach(fitTableToWidth);
    structuralChange();
  }
  /** 인라인 링크 <a> → 북마크 블록(카드/임베드) 전환. link-toolbar 의 "카드/임베드" 버튼이 호출. */
  function convertLinkToBookmark(anchor, mode) {
    if (!editable || !anchor || !anchor.getAttribute) return false;
    const url = (anchor.getAttribute('href') || '').trim();
    if (!url) return false;
    const block = anchor.closest('.block');
    if (!block || !editorEl.contains(block)) return false;
    if (anchor.closest('td.lumir-cell')) return false; // 표 셀 내부 링크는 전환 제외(구조 복잡)
    const content = anchor.closest('.content');
    const props = { url, mode: mode === 'embed' ? 'embed' : 'card' };
    if (props.mode === 'embed') {
      const emb = resolveEmbed(url);
      if (emb) {
        props.provider = emb.provider;
        props.embedUrl = emb.embedUrl;
        props.ratio = emb.ratio;
      } else props.mode = 'card'; // 임베드 불가 → 카드
    }
    const el = renderBlock({ type: 'bookmark', props, content: [], children: [] });
    // 링크가 블록 텍스트 전부면 블록 교체, 아니면 블록 뒤에 삽입(원문 링크 보존).
    const onlyLink =
      content && (content.textContent || '').trim() === (anchor.textContent || '').trim();
    if (onlyLink) block.replaceWith(el);
    else block.after(el);
    if (trailingBlock && !el.nextElementSibling)
      el.after(
        renderBlock({
          type: 'paragraph',
          props: DEFAULT_BLOCK_PROPS(),
          content: [],
          children: [],
        }),
      );
    refreshFeatureBlocks();
    // 카드: 서버(fetchLinkPreview)로 메타 비동기 채움. 미주입/실패 시 URL·도메인만 있는 카드로 유지.
    if (props.mode === 'card' && typeof opts.fetchLinkPreview === 'function') {
      const wrap = el.querySelector('.lumir-bookmark');
      if (wrap) wrap.classList.add('is-loading');
      Promise.resolve()
        .then(() => opts.fetchLinkPreview(url))
        .then((meta) => {
          if (!meta || !el.isConnected) {
            if (wrap) wrap.classList.remove('is-loading');
            return;
          }
          const merged = { ...el.__props };
          ['title', 'description', 'image', 'site', 'favicon'].forEach((k) => {
            if (meta[k]) merged[k] = String(meta[k]);
          });
          const newEl = renderBlock({ type: 'bookmark', props: merged, content: [], children: [] });
          el.replaceWith(newEl);
          refreshFeatureBlocks();
          commit();
          fireChange();
        })
        .catch(() => {
          if (wrap) wrap.classList.remove('is-loading');
        });
    }
    commit();
    fireChange();
    return true;
  }
  /** 붙여넣은 표의 열폭 합이 에디터 폭을 넘으면 비례 축소(모바일은 가로 스크롤 정책 → 축소 생략) */
  function fitTableToWidth(el) {
    const table = el.querySelector('.lumir-tbl');
    if (!table || !table.__model) return;
    // 모바일(max-width:640px): 폭 맞춤 대신 가로 스크롤 — table.css의 브레이크포인트와 일치.
    if (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 640px)').matches
    )
      return;
    const maxW = editorEl.clientWidth || 0;
    if (!maxW) return;
    const fitted = fitColumnWidths(table.__model.columnWidths, maxW);
    if (fitted !== table.__model.columnWidths) {
      table.__model.columnWidths = fitted;
      rerenderTable(table);
    }
  }
  /** 단독 URL 붙여넣기 → 항상 인라인 링크(선택→감싸기, 커서→삽입). */
  function handleUrlPaste(rawUrl) {
    const url = String(rawUrl || '').trim();
    if (!url) return false;
    const content = focusedContent();
    if (!content) return false;
    const sel = window.getSelection();
    const r = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    const hasSelection = !!(r && !r.collapsed);
    const ok = hasSelection
      ? applyLink(content, url)
      : insertInlineAtCursor(content, [
          {
            type: 'link',
            href: url,
            content: [{ type: 'text', text: url, styles: {} }],
          },
        ]);
    if (ok) {
      structuralChange();
      return true;
    }
    return false;
  }
  /** 다단어 텍스트 내 http(s) URL 자동 링크화(커서에 인라인 삽입). */
  function autolinkTextPaste(text) {
    const content = focusedContent();
    if (!content) return false;
    const nodes = linkifyToInline(text);
    if (!nodes.some((n) => n.type === 'link')) return false;
    if (insertInlineAtCursor(content, nodes)) {
      structuralChange();
      return true;
    }
    return false;
  }
  function handlePasteContent(html, text) {
    // URL 붙여넣기 통합 규칙 — 단독 URL(html 없음)
    if (!html && isBareUrl(text)) {
      if (handleUrlPaste(text)) return true;
    }
    // 다단어 텍스트 내 URL 자동 링크화 — 단일 라인, html 없음
    if (!html && !/\n/.test(text) && hasUrl(text) && !isBareUrl(text)) {
      if (autolinkTextPaste(text)) return true;
    }
    // 기능 슬라이스 붙여넣기 규칙 우선
    for (const rule of registry.gatherPaste({
      insertBlocks: insertBlocksAtCursor,
    })) {
      if (rule && rule.match(text, html) && rule.handle(text, html))
        return true;
    }
    if (html) {
      const blocks = parseHtmlToBlocks(normalizeExcelClipboardHtml(html)); // 엑셀 클래스색 computed 정규화 후 파싱
      if (
        blocks.length > 1 ||
        (blocks.length === 1 && blocks[0].type !== 'paragraph')
      ) {
        // base64/blob/로컬 미디어 제외 후 삽입 → data:/blob: 이미지는 서버로 업로드해 URL 교체(타 PC 정합·DB base64 방지).
        const { blocks: kept, dropped } = filterPasteMedia(blocks);
        const els = insertBlocksAtCursor(kept);
        uploadPastedImages(els); // 비동기(fire-and-forget) — 진행률 오버레이 표시 후 제자리 스왑/실패 시 제거
        if (dropped) errorReporter.showToast(L.paste.imagesDropped);
        return true; // dropped 로 kept 가 비어도 consumed(네이티브가 깨진 미디어를 붙이지 않도록)
      }
      return false; // 단일 문단 → 네이티브 인라인 붙여넣기
    }
    if (/\n/.test(text)) {
      const blocks = blocksFromText(text);
      if (blocks.length) {
        insertBlocksAtCursor(blocks);
        return true;
      }
    }
    return false;
  }
  editorEl.addEventListener('paste', (e) => {
    if (!editable) return;
    const cd = e.clipboardData;
    if (!cd) return;
    if (e.target && e.target.closest && e.target.closest('td.lumir-cell'))
      return; // 표 셀 붙여넣기는 네이티브(추후)
    // 크로스 선택 위 붙여넣기 → 먼저 선택 삭제(커밋 없이) 후 join 캐럿에서 기존 삽입 경로 진행
    // (삽입 경로가 1회 커밋 → 삭제+삽입이 단일 undo 스텝).
    if (crossSelect && crossSelect.isActive()) {
      const caret = deleteRangeAcross(editorEl, crossSelect.get());
      crossSelect.clear();
      if (caret && caret.island) {
        try {
          caret.island.focus();
          setSelection(caret.island, caret.offset, caret.offset);
        } catch (_) {}
      }
    }
    // 무손실 lumir-JSON(다중 블록 복제)을 최우선 인식 — <table> html 하이재킹보다 먼저 처리(표 포함 복제 보장).
    // 커스텀 MIME(동기 copy) 우선, 없으면 비동기 clipboard가 html에 임베드한 JSON을 회수.
    let lumirJson = cd.getData ? cd.getData(LUMIR_BLOCK_COPY_MIME) : '';
    if (!lumirJson) lumirJson = extractEmbeddedBlocks(cd.getData ? cd.getData('text/html') : '');
    if (lumirJson && handleLumirBlocksPaste(lumirJson)) {
      e.preventDefault();
      return;
    }
    const html = cd.getData ? cd.getData('text/html') : '';
    const text = cd.getData ? cd.getData('text/plain') || '' : '';
    // Excel/시트 하이재킹 방지: html에 <table>이 있으면 동봉된 비트맵 업로드보다 표 파싱을 우선.
    if (/<table[\s>]/i.test(html)) {
      if (handlePasteContent(html, text)) {
        e.preventDefault();
        return;
      }
    }
    // 클립보드 파일(스크린샷/이미지 파일) → 업로드
    const items = cd.items ? [...cd.items] : [];
    const files = items
      .filter((it) => it.kind === 'file')
      .map((it) => it.getAsFile())
      .filter(Boolean);
    const media = files.filter(isMedia);
    if (media.length) {
      e.preventDefault();
      insertFiles(media);
      return;
    }
    // 그 외: html(이미지 <img>·표 등 구조 포함) / 멀티라인 텍스트
    if (handlePasteContent(html, text)) e.preventDefault();
  });
  document.body.appendChild(uploadOverlay.overlayEl);
  document.body.appendChild(fileInput);
  document.body.appendChild(errorReporter.toastEl);

  /** 페이지 나누기 블록 삽입(슬래시·마크다운 공용) */
  function insertPageBreak() {
    insertBlocksAtCursor([
      { type: 'pageBreak', props: {}, content: [], children: [] },
    ]);
  }

  // --- 편집 동작(Enter/Backspace) + 슬래시 메뉴 — IME 가드, 구조 변경 후 히스토리 커밋 ---
  const structuralChange = () => {
    normalizeColumnsDomEl(editorEl);
    commit();
    fireChange();
  };
  let detachEditing = null,
    detachCaretNav = null,
    blockSelect = null,
    crossSelect = null,
    crossFormat = null,
    crossEdit = null,
    slash = null,
    formatToolbar = null,
    tableControls = null,
    featureControlHandles = [],
    emojiPicker = null,
    tableSizePicker = null;
  let filePanel = null;
  if (editable) {
    // 크로스블록(여러 줄) 선택 — caret-nav/editing보다 먼저 등록해 방향키/Escape를 우선 처리.
    crossSelect = attachCrossBlockSelect(editorEl, {
      isEditable: () => editable,
      isComposing: () => composing,
      clearBlockSelect: () => {
        if (blockSelect) blockSelect.clear();
      },
      onSettled: () => {
        if (formatToolbar) formatToolbar.update();
        if (floatingMenu && floatingMenu.sync) floatingMenu.sync();
      },
    });
    // 크로스 서식 파사드 — 툴바/단축키가 (드래그 크로스 선택 | Ctrl+A 블록 전체 선택)일 때 이 경로로 라우팅.
    // 커버 섬: 드래그 크로스면 부분/전체 혼합, 블록 전체 선택이면 선택 블록들의 .content 전체(일반 텍스트만).
    const coveredForFormat = () => {
      if (crossSelect.isActive()) return crossSelect.coveredIslands();
      if (blockSelect && blockSelect.isActive() && blockSelect.selectedIslands) {
        return blockSelect.selectedIslands().map((island) => ({ island, start: 0, end: contentLen(island), full: true }));
      }
      return [];
    };
    crossFormat = {
      isActive: () => crossSelect.isActive() || !!(blockSelect && blockSelect.isActive()),
      // Ctrl+A 전체 선택(block-select 모드)인가 — 툴바를 에디터 중상단에 고정 배치하기 위한 힌트.
      isBlockSelect: () => !crossSelect.isActive() && !!(blockSelect && blockSelect.isActive()),
      isDragging: () => crossSelect.isDragging(),
      applyMark: (type, value) => {
        const cov = coveredForFormat();
        if (applyInlineMarkAcross(cov, type, value)) {
          structuralChange();
          if (crossSelect.isActive()) crossSelect.repaint(); // 드래그 크로스는 커스텀 하이라이트 재페인팅(블록 선택은 CSS 클래스라 유지)
        }
      },
      readMarks: () => readActiveMarksAcross(coveredForFormat()),
      boundingRect: () => (crossSelect.isActive() ? crossSelect.boundingRect() : crossBoundingRect(coveredForFormat())),
    };
    if (opts.filePanel !== false)
      filePanel = attachFilePanel(editorEl, {
        onPickFile: () => openImagePicker(),
        onInsertBlock: (b) => insertBlocksAtCursor([b]),
      });
    // 크로스 선택 영역 편집(삭제/타이핑/Enter/복사·잘라내기) — editing/caret-nav보다 먼저 등록.
    crossEdit = attachCrossBlockEdit(editorEl, {
      isEditable: () => editable,
      isComposing: () => composing,
      commitChange: structuralChange,
      isActive: () => crossSelect.isActive(),
      getSelection: () => crossSelect.get(),
      clearCross: () => crossSelect.clear(),
      getDocument,
      htmlOf: (blocks) => blocksToHtml(blocks),
      textOf: (blocks) => blocksToMarkdown(blocks),
      jsonOf: (blocks) => JSON.stringify(blocks),
    });
    detachEditing = attachEditing(editorEl, {
      onChange: structuralChange,
      isComposing: () => composing,
      headingLevels,
      tabBehavior,
      cross: crossFormat,
    });
    // 방향키 블록 간 이동 + 인라인 코드 탈출
    detachCaretNav = attachCaretNav(editorEl, {
      isComposing: () => composing,
      onChange: structuralChange,
    });
    // Ctrl/Cmd+A 단계 선택(현재 블록 → 전체 블록) + 블록 선택 모드(삭제/복사)
    blockSelect = attachBlockSelect(editorEl, {
      isEditable: () => editable,
      isComposing: () => composing,
      getDocument,
      htmlOf: (blocks) => blocksToHtml(blocks),
      textOf: (blocks) => blocksToMarkdown(blocks),
      jsonOf: (blocks) => JSON.stringify(blocks), // 무손실 라운드트립용 원본 JSON(저장 폼)

      makeParagraphEl: () =>
        renderBlock({
          type: 'paragraph',
          props: DEFAULT_BLOCK_PROPS(),
          content: [],
          children: [],
        }),
      commitChange: structuralChange,
    });
    const SM = L.slashMenu || {};
    const gEmbed = SM.groupEmbed || '임베드';
    // 기능 슬라이스(features/*) 슬래시 기여 수집 — pageBreak/image/file 등. (미이전: table/columns 는 레거시 배선)
    const featureCtx = {
      locale: L,
      insertBlocks: insertBlocksAtCursor,
      openImagePicker,
      filePanel,
      hasUploader: !!uploader, // uploadFile/s3Upload 설정 여부 = 이미지 업로드 슬래시 노출 게이트
      opts,
    };
    const extraItems = [...registry.gatherSlash(featureCtx)];
    // 이모지 — 기본 활성(emojiPicker !== false). 슬래시 "이모지"로 피커 열기.
    if (opts.emojiPicker !== false) {
      emojiPicker = attachEmojiPicker(editorEl, { onChange: structuralChange });
      extraItems.push({
        type: 'emoji',
        label: SM.emoji || '이모지',
        kw: 'emoji 이모지 emoticon 스마일 이모티콘',
        group: SM.groupBasic || '기본 블록',
        ico: '😀',
        sub: '이모지 삽입',
        action: (b) => emojiPicker.open(b),
      });
    }
    // 표 크기 N×M 그리드 피커 — 슬래시 "표"/플로팅 메뉴가 공유. 열 상한은 에디터 폭 기반.
    tableSizePicker = attachTableSizePicker(editorEl, {
      locale: L,
      onPick: (rows, cols) => insertBlocksAtCursor([emptyTable(rows, cols)]),
      getMaxCols: () => {
        const cw = editorEl.clientWidth || 0;
        return cw ? Math.max(3, Math.floor((cw - 28) / 48)) : 20;
      },
    });
    extraItems.push({
      type: 'table',
      label: SM.table || '표',
      kw: 'table 표 격자 grid 셀',
      group: gEmbed,
      ico: '▦',
      sub: 'N×M 크기를 골라 표 추가',
      action: () => tableSizePicker.open(),
    });
    extraItems.push({
      type: 'columnList',
      label: SM.columns2 || '2단 컬럼',
      kw: 'column 컬럼 2단 단 layout 나누기 col',
      group: gEmbed,
      ico: '▦',
      sub: '블록을 좌우 2단으로 배치',
      action: () => insertBlocksAtCursor([emptyColumns(2)]),
    });
    extraItems.push({
      type: 'columnList',
      label: SM.columns2Divider || '2단 컬럼 (구분선)',
      kw: 'column 컬럼 2단 구분선 divider layout col',
      group: gEmbed,
      ico: COLUMNS_DIVIDER_ICON,
      sub: '가운데 구분선이 있는 2단',
      action: () => insertBlocksAtCursor([emptyColumns(2, true)]),
    });
    if (opts.slashMenu !== false)
      slash = attachSlashMenu(editorEl, {
        onApply: structuralChange,
        isComposing: () => composing,
        headingLevels,
        extraItems,
        locale: L,
      });
    // 선택 시 인라인 서식 팝업은 항상 표시(vanilla는 동기 렌더라 floatingMenu와 공존해도 (0,0) 튐 없음).
    if (opts.formattingToolbar !== false)
      formatToolbar = attachFormatToolbar(editorEl, {
        onChange: structuralChange,
        isComposing: () => composing,
        headingLevels,
        locale: L,
        onLink: () => linkToolbar && linkToolbar.promptAdd(),
        cross: crossFormat,
      });
    if (opts.tableHandles !== false)
      tableControls = attachTableControls(editorEl, {
        onChange: structuralChange,
        tables: cfgTables,
        locale: L,
      });
  }
  // 기능 슬라이스 인터랙션 컨트롤러 — 읽기전용에서도 부착(미디어 열기/다운로드 유지).
  // 리사이즈 등 편집 동작은 각 컨트롤러가 ctx.isEditable() 로 가드. image/video 등.
  featureControlHandles = registry.attachControls(editorEl, {
    onChange: structuralChange,
    isEditable: () => !editorEl.classList.contains('lumir-readonly'),
  });
  /** 기능 컨트롤러 refresh — 삽입/붙여넣기 후 호출 */
  function refreshFeatureBlocks() {
    featureControlHandles.forEach((h) => {
      try {
        h && h.refresh && h.refresh();
      } catch (_) {}
    });
  }

  // 링크 툴바 (링크 hover 시 툴팁: URL/복사/편집 → 편집 popup: URL·제목·링크제거. 선택 텍스트에 링크 추가.)
  let linkToolbar = null;
  if (editable && opts.linkToolbar !== false) {
    linkToolbar = attachLinkToolbar(editorEl, {
      onChange: structuralChange,
      locale: L,
      onConvert: convertLinkToBookmark,
      canEmbed: (href) => !!resolveEmbed(href),
    });
  }
  // 상단 FloatingMenu (원본과 동일하게 기본 비활성; opts.floatingMenu:true 로 활성)
  let floatingMenu = null;
  if (editable && opts.floatingMenu === true) {
    floatingMenu = attachFloatingMenu(editorEl, {
      locale: L,
      headingLevels,
      position: opts.floatingMenuPosition === 'fixed' ? 'fixed' : 'sticky',
      undo,
      redo,
      canUndo,
      canRedo,
      onChange: structuralChange,
      insertImage: uploader ? () => openImagePicker() : null, // 업로더 없으면 플로팅 메뉴 이미지 버튼 숨김
      insertTable: () =>
        tableSizePicker
          ? tableSizePicker.open()
          : insertBlocksAtCursor([emptyTable(3, 3)]),
      insertLink: linkToolbar ? () => linkToolbar.promptAdd() : null,
      cross: crossFormat,
    });
  }

  // 사이드 메뉴 / 드래그 핸들 (기본 ON; opts.sideMenu===false 로 비활성). 블록 추가는 그립 드롭다운으로만(+ 버튼 제거).
  let sideMenu = null;
  if (editable && opts.sideMenu !== false) {
    sideMenu = attachSideMenu(editorEl, { onChange: structuralChange, onDuplicate: duplicateBlockEl });
  }
  // 컬럼 너비 리사이즈 (P9-⑦)
  let columnControls = null;
  if (editable)
    columnControls = attachColumnControls(editorEl, {
      onChange: structuralChange,
    });

  // onSelectionChange (P8-②) — 에디터 내 선택 변경 시 {block, blockType, isCollapsed, text, marks}
  function fireSelectionChange() {
    if (!onSelectionChange) return;
    const sel = document.getSelection();
    if (!sel || !sel.rangeCount) return;
    const r = sel.getRangeAt(0);
    let n = r.startContainer;
    if (n && n.nodeType === 3) n = n.parentNode;
    const content =
      n && n.closest ? n.closest('.content, td.lumir-cell') : null;
    if (!content || !editorEl.contains(content)) return; // 에디터 밖 선택은 무시
    const block = content.closest('.block');
    onSelectionChange({
      block: block ? block.dataset.id || null : null,
      blockType: block ? block.dataset.type : null,
      isCollapsed: r.collapsed,
      text: sel.toString(),
      marks: readActiveMarks(content),
    });
  }

  // blur 선택 하이라이트 (P9-⑥) — CSS Custom Highlight API(미지원 환경은 무시)
  const blurHl = createBlurHighlight(editorEl);

  // 초기 적용 + placeholder/선택 갱신 리스너
  applyEditable(editable);
  refreshPlaceholders();
  const onSelChange = () => {
    refreshPlaceholders();
    blurHl.capture();
    fireSelectionChange();
  };
  document.addEventListener('selectionchange', onSelChange);

  return {
    element: editorEl,
    getDocument,
    getMarkdown: () => blocksToMarkdown(getDocument()),
    getHTML: () => blocksToHtml(getDocument()),
    getFullHTML: (htmlOpts) => blocksToFullHtml(getDocument(), htmlOpts),
    setBlocks(blocks) {
      restore(Array.isArray(blocks) ? blocks : []);
      commit();
      refreshFeatureBlocks();
      fireChange();
    },
    setMarkdown(md) {
      restore(markdownToBlocks(md));
      commit();
      refreshFeatureBlocks();
      fireChange();
    },
    commit,
    undo,
    redo,
    canUndo,
    canRedo,
    isComposing: () => composing,
    setOnContentChange(fn) {
      onChange = typeof fn === 'function' ? fn : null;
    },
    isSlashOpen: () => (slash ? slash.isOpen() : false),
    insertFile,
    formatToolbar,
    tableControls,
    floatingMenu,
    sideMenu,
    columnControls,
    linkToolbar,
    filePanel,
    emojiPicker,
    isEditable: () => editable,
    setEditable(on) {
      editable = on !== false;
      applyEditable(editable);
      refreshPlaceholders();
    },
    setTheme(theme) {
      applyThemeOpt(theme);
    },
    destroy() {
      document.removeEventListener('selectionchange', onSelChange);
      if (detachEditing) detachEditing();
      if (detachCaretNav) detachCaretNav();
      if (crossEdit) crossEdit.destroy();
      if (crossSelect) crossSelect.destroy();
      if (blockSelect) blockSelect.destroy();
      if (slash) slash.destroy();
      if (formatToolbar) formatToolbar.destroy();
      if (tableControls) tableControls.destroy();
      featureControlHandles.forEach((h) => {
        try {
          h && h.destroy && h.destroy();
        } catch (_) {}
      });
      if (floatingMenu) floatingMenu.destroy();
      if (sideMenu) sideMenu.destroy();
      if (columnControls) columnControls.destroy();
      if (linkToolbar) linkToolbar.destroy();
      if (filePanel) filePanel.destroy();
      if (emojiPicker) emojiPicker.destroy();
      if (tableSizePicker) tableSizePicker.destroy();
      blurHl.destroy();
      uploadOverlay.destroy();
      errorReporter.destroy();
      if (fileInput.parentNode) fileInput.parentNode.removeChild(fileInput);
      if (editorEl.parentNode) editorEl.parentNode.removeChild(editorEl);
    },
  };
}
